"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";

const consentLevels = [
  {
    level: "Full Access",
    icon: "CheckCircleIcon",
    description: "Professional sees complete pet profile including medical history, allergies, medications, and emergency contacts.",
    color: "#0EA5A0",
    bg: "#E0F7F6",
    example: "Your primary veterinarian",
  },
  {
    level: "Limited Access",
    icon: "AdjustmentsHorizontalIcon",
    description: "Professional sees only specific sections you choose—like grooming notes and appointment history, but not medical records.",
    color: "#7C3AED",
    bg: "#EDE9FE",
    example: "Your dogfob groomer or trainer",
  },
  {
    level: "Emergency Only",
    icon: "ExclamationTriangleIcon",
    description: "Professional can only access basic info and emergency contacts if your pet is lost or in distress.",
    color: "#DC2626",
    bg: "#FEE2E2",
    example: "Pet sitters or dogfob walkers",
  },
];

export default function ConsentModel() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeLevel, setActiveLevel] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24"
      style={{ background: "linear-gradient(160deg, #F0FAFB 0%, #F7F8F6 100%)" }}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-16 scroll-fade-up">
          <span className="teal-badge mb-4">Consent Model</span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] mt-4 mb-5 tracking-tight">
            You control who sees what
          </h2>
          <p className="text-lg text-[#4A6363] max-w-2xl mx-auto leading-relaxed">
            DogFob uses granular, consent-based sharing. Every professional you connect with gets only the access level you explicitly grant.
          </p>
        </div>

        {/* Consent Levels */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {consentLevels.map((level, idx) => (
            <div
              key={idx}
              className={`scroll-fade-up stagger-${idx + 1} feature-card p-6 cursor-pointer transition-all duration-300 ${
                activeLevel === idx ? "ring-2 ring-teal-500 shadow-teal" : ""
              }`}
              onClick={() => setActiveLevel(idx)}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ background: level.bg }}
              >
                <Icon
                  name={level.icon as "CheckCircleIcon"}
                  size={20}
                  style={{ color: level.color }}
                />
              </div>
              <h3 className="text-lg font-bold text-[#1A2B2B] font-heading mb-2">
                {level.level}
              </h3>
              <p className="text-sm text-[#4A6363] leading-relaxed mb-3">
                {level.description}
              </p>
              <div
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
                style={{ background: level.bg, color: level.color }}
              >
                <Icon name="UserIcon" size={12} />
                {level.example}
              </div>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="scroll-fade-up feature-card p-8">
          <h3 className="text-2xl font-bold text-[#1A2B2B] font-heading mb-6 text-center">
            How consent works in practice
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mb-4">
                <Icon name="UserPlusIcon" size={24} className="text-teal-500" />
              </div>
              <h4 className="text-base font-bold text-[#1A2B2B] font-heading mb-2">
                1. Send Connection Request
              </h4>
              <p className="text-sm text-[#4A6363] leading-relaxed">
                Search for a professional and send them a connection request from your pet's profile.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center mb-4">
                <Icon name="AdjustmentsHorizontalIcon" size={24} className="text-purple-600" />
              </div>
              <h4 className="text-base font-bold text-[#1A2B2B] font-heading mb-2">
                2. Choose Access Level
              </h4>
              <p className="text-sm text-[#4A6363] leading-relaxed">
                Select which sections they can view: medical, grooming, training, or emergency only.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                <Icon name="ArrowPathIcon" size={24} className="text-blue-600" />
              </div>
              <h4 className="text-base font-bold text-[#1A2B2B] font-heading mb-2">
                3. Update Anytime
              </h4>
              <p className="text-sm text-[#4A6363] leading-relaxed">
                Change permissions or revoke access instantly from your dashboard. No questions asked.
              </p>
            </div>
          </div>
        </div>

        {/* Trust Note */}
        <div className="mt-10 scroll-fade-up flex items-center justify-center gap-3 text-center">
          <Icon name="ShieldCheckIcon" size={20} className="text-teal-500" />
          <p className="text-sm text-[#4A6363]">
            Professionals are notified when access levels change and can only see data within their granted permissions.
          </p>
        </div>
      </div>
    </section>
  );
}